import { useMemo } from 'react';

import { OZ_PER_GALLON, OZ_PER_UNIT, VOLUME_RATE_UNITS } from './constants';
import type {
    MixApplyCalcsInput,
    MixApplyCalcsOutput,
    MixApplyCalcsProduct,
    ProductAmount
} from './types';

const computeProductAmount = (
    product: MixApplyCalcsProduct,
    acresPerTank: number,
    totalAcres: number
): ProductAmount => {
    const ratePerAcInOz = product.rate * OZ_PER_UNIT[product.rateUnit];

    return {
        perMixTankOz: acresPerTank * ratePerAcInOz,
        productId: product.productId,
        productName: product.productName,
        totalJobOz: totalAcres * ratePerAcInOz
    };
};

// Volume displacement is a pure volume→volume calculation: 32 fl oz/acre is
// 32 fl oz/acre regardless of what the product weighs per gallon. The only
// thing that matters for tank-fill math is whether the rate unit is a volume
// unit. Dry-mass rates (lb/ac) don't displace water in the mix tank, so they
// contribute zero. Density is NOT consulted here — it's used elsewhere for
// mass-of-AI math, but not for volume.
const volumeContributionGal = (product: MixApplyCalcsProduct, totalAcres: number): number => {
    if (!VOLUME_RATE_UNITS.has(product.rateUnit)) {
        return 0;
    }
    const totalOz = product.rate * OZ_PER_UNIT[product.rateUnit] * totalAcres;

    return totalOz / OZ_PER_GALLON;
};

const useMixApplyCalcs = (input: MixApplyCalcsInput): MixApplyCalcsOutput => {
    const { appTankGal, carrierRateGalPerAc, mixTankGal, products, totalAcres } = input;

    return useMemo(() => {
        const totalSprayVolumeGal = totalAcres * carrierRateGalPerAc;
        const batches = mixTankGal > 0 ? Math.ceil(totalSprayVolumeGal / mixTankGal) : 0;
        const fillsPerBatch = appTankGal > 0 ? Math.ceil(mixTankGal / appTankGal) : 0;
        const totalAppFills = appTankGal > 0 ? Math.ceil(totalSprayVolumeGal / appTankGal) : 0;
        const acresPerTank = carrierRateGalPerAc > 0 ? mixTankGal / carrierRateGalPerAc : 0;
        const partialLastTankGal = appTankGal > 0 ? totalSprayVolumeGal % appTankGal : 0;
        const hasPartialTank = partialLastTankGal > 0;

        const productVolumeGal = products.reduce(
            (sum, product) => sum + volumeContributionGal(product, totalAcres),
            0
        );
        const waterVolumeGal = totalSprayVolumeGal - productVolumeGal;
        const carrierSufficient = waterVolumeGal >= 0;
        const productConcentrationPct =
            totalSprayVolumeGal > 0 ? (productVolumeGal / totalSprayVolumeGal) * 100 : 0;

        const productAmounts = products.map(product =>
            computeProductAmount(product, acresPerTank, totalAcres)
        );

        return {
            acresPerTank,
            batches,
            carrierSufficient,
            fillsPerBatch,
            hasPartialTank,
            partialLastTankGal,
            productAmounts,
            productConcentrationPct,
            productVolumeGal,
            totalAppFills,
            totalSprayVolumeGal,
            waterVolumeGal
        };
    }, [appTankGal, carrierRateGalPerAc, mixTankGal, products, totalAcres]);
};

export default useMixApplyCalcs;
